import React from 'react';
import { Wallet, LineChart, Target, Gauge } from 'lucide-react';
import { useSettings } from '../../../lib/settings/settingsContext.tsx';
import { AdminUserOverview } from "../../../services/adminUserService.ts";

interface AdminUserSummaryCardsProps {
  overview: AdminUserOverview;
  portfolioValue: number;
  winRate: number;
  openPositions: number;
  totalTrades: number;
}

export function AdminUserSummaryCards({
  overview,
  portfolioValue,
  winRate,
  openPositions,
  totalTrades,
}: AdminUserSummaryCardsProps) {
  const { formatCurrency } = useSettings();

  const normalizedWinRate = Number.isFinite(winRate) ? winRate : 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
      <SummaryCard
        title="Wallet Balance"
        value={formatCurrency(overview.walletBalance)}
        icon={<Wallet className="w-6 h-6 text-wayja-teal" />}
        accent="bg-wayja-teal/10"
      />
      <SummaryCard
        title="Portfolio Value"
        value={formatCurrency(portfolioValue)}
        icon={<LineChart className="w-6 h-6 text-blue-600" />}
        accent="bg-blue-50"
      />
      <SummaryCard
        title="Open Positions"
        value={openPositions.toString()}
        icon={<Gauge className="w-6 h-6 text-green-600" />}
        accent="bg-green-50"
      />
      <SummaryCard
        title="Win Rate"
        value={`${normalizedWinRate.toFixed(1)}%`}
        icon={<Target className="w-6 h-6 text-purple-600" />}
        accent="bg-purple-50"
        helper={`${totalTrades} total trades`}
      />
    </div>
  );
}

interface SummaryCardProps {
  title: string;
  value: string;
  icon: React.ReactNode;
  accent: string;
  helper?: string;
}

function SummaryCard({ title, value, icon, accent, helper }: SummaryCardProps) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-semibold text-gray-900 mt-1">{value}</p>
          {helper && <p className="text-xs text-gray-500 mt-2">{helper}</p>}
        </div>
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${accent}`}>
          {icon}
        </div>
      </div>
    </div>
  );
}

